import React, { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useFeatures } from '../features';
import { useSnackbar } from '../components/common/SnackbarProvider';

const FeatureFlagsAdminPage: React.FC = () => {
  const { list, loading, error, refresh, setFlag } = useFeatures();
  const snackbar = useSnackbar();
  const [busyKey, setBusyKey] = useState<string | null>(null);

  async function toggle(key: string, enabled: boolean) {
    setBusyKey(key);
    try {
      await setFlag(key, enabled);
      snackbar.success(`${key} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Failed to update flag');
      void refresh();
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, sm: 0 }}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Feature Flags
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Toggle club program domains on or off. Flipping a flag immediately hides or shows the relevant
              member, staff, and admin pages. Backend data is never affected — turn a domain back on whenever
              you're ready to ship it.
            </Typography>
          </Box>
          <IconButton onClick={() => void refresh()} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {loading && list.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {list.map((flag, i) => (
              <Card key={flag.key} variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6">{flag.label}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {flag.key}
                        </Typography>
                      </Stack>
                      {flag.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {flag.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Last updated {flag.updated_at ? new Date(flag.updated_at).toLocaleString() : '—'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography
                        variant="body2"
                        color={flag.enabled ? 'success.main' : 'text.secondary'}
                        sx={{ fontWeight: 600, minWidth: 32 }}
                      >
                        {flag.enabled ? 'On' : 'Off'}
                      </Typography>
                      <Switch
                        checked={flag.enabled}
                        onChange={(e) => void toggle(flag.key, e.target.checked)}
                        disabled={busyKey === flag.key}
                        color="success"
                      />
                    </Stack>
                  </Stack>
                </CardContent>
                {i < list.length - 1 && <Divider />}
              </Card>
            ))}
          </Stack>
        )}
      </Box>
    </Container>
  );
};

export default FeatureFlagsAdminPage;
